import { ActionNode, ControlNode } from "@/shared/ui/composites";
import { Button, HStack, Icon, VStack } from "@/shared/ui/primitives";
import { useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type WorkflowNode =
	| { type: "action"; x: number; y: number; name: string; description: string; gyulAmount: number }
	| { type: "control"; x: number; y: number; name: string; description: string };

const connections = [
	{ from: 0, to: 1, delay: 0.3 },
	{ from: 1, to: 2, delay: 0.9 },
	{ from: 1, to: 3, delay: 0.9 },
	{ from: 1, to: 4, delay: 0.9 },
];

export const HeroSection = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const containerRef = useRef<HTMLDivElement>(null);
	const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
	const [paths, setPaths] = useState<string[]>([]);

	const nodeDelays = [0, 0.6, 1.2, 1.2, 1.2];

	const nodes: WorkflowNode[] = [
		{
			type: "action",
			x: 0,
			y: 100,
			name: t("home.hero.flow.start.name"),
			description: t("home.hero.flow.start.description"),
			gyulAmount: 1,
		},
		{
			type: "control",
			x: 320,
			y: 100,
			name: t("home.hero.flow.design.name"),
			description: t("home.hero.flow.design.description"),
		},
		{
			type: "action",
			x: 640,
			y: -20,
			name: t("home.hero.flow.execute.name"),
			description: t("home.hero.flow.execute.description"),
			gyulAmount: 3,
		},
		{
			type: "action",
			x: 640,
			y: 100,
			name: t("home.hero.flow.alert.name"),
			description: t("home.hero.flow.alert.description"),
			gyulAmount: 1,
		},
		{
			type: "control",
			x: 640,
			y: 220,
			name: t("home.hero.flow.done.name"),
			description: t("home.hero.flow.done.description"),
		},
	];

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const containerRect = container.getBoundingClientRect();
		const newPaths = connections.map(({ from, to }) => {
			const fromEl = nodeRefs.current[from];
			const toEl = nodeRefs.current[to];
			if (!fromEl || !toEl) return "";

			const fromRect = fromEl.getBoundingClientRect();
			const toRect = toEl.getBoundingClientRect();

			const x1 = fromRect.right - containerRect.left;
			const y1 = fromRect.top + fromRect.height / 2 - containerRect.top;
			const x2 = toRect.left - containerRect.left;
			const y2 = toRect.top + toRect.height / 2 - containerRect.top;

			return `M ${x1},${y1} L ${x2},${y2}`;
		});

		setPaths(newPaths);
	}, []);

	const mobileNodes = [
		{ type: "action" as const, name: t("home.hero.flow.start.name"), description: t("home.hero.flow.start.description"), gyulAmount: 1 },
		{ type: "control" as const, name: t("home.hero.flow.design.name"), description: t("home.hero.flow.design.description") },
		{ type: "action" as const, name: t("home.hero.flow.execute.name"), description: t("home.hero.flow.execute.description"), gyulAmount: 3 },
		{ type: "action" as const, name: t("home.hero.flow.alert.name"), description: t("home.hero.flow.alert.description"), gyulAmount: 1 },
		{ type: "control" as const, name: t("home.hero.flow.done.name"), description: t("home.hero.flow.done.description") },
	];

	return (
		<VStack className="w-full gap-[36px] md:gap-[28px] py-[40px] md:py-0 md:min-h-[600px] items-center z-20">
			{/* 타이틀 영역 */}
			<VStack className="gap-[10px]">
				<motion.h1
					className="text-[1.5rem] md:text-[2.25rem] text-ct-strong font-semibold text-center"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
				>
					{t("home.hero.title")}
				</motion.h1>
				<motion.p
					className="text-lg text-ct-regular font-medium text-center"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.2 }}
				>
					{t("home.hero.subtitle")}
				</motion.p>
			</VStack>

			{/* 데스크톱: 워크플로우 그래프 */}
			<div ref={containerRef} className="relative w-full max-w-[1000px] h-[300px] hidden md:block">
				<svg className="absolute inset-0 w-full h-full overflow-visible pointer-events-none">
					{paths.map((d, i) =>
						d ? (
							<motion.path
								key={i}
								d={d}
								stroke="var(--ct-weak)"
								strokeWidth="2"
								fill="none"
								initial={{ pathLength: 0 }}
								animate={{ pathLength: 1 }}
								transition={{ duration: 0.5, delay: connections[i].delay, ease: "easeInOut" }}
							/>
						) : null,
					)}
				</svg>

				{nodes.map((node, index) => (
					<motion.div
						key={index}
						ref={(el) => {
							nodeRefs.current[index] = el;
						}}
						style={{ position: "absolute", left: node.x, top: node.y }}
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{
							duration: 0.5,
							delay: nodeDelays[index],
							ease: "easeOut",
						}}
					>
						{node.type === "action" ? (
							<ActionNode
								name={node.name}
								description={node.description}
								gyulAmount={node.gyulAmount}
							/>
						) : (
							<ControlNode name={node.name} description={node.description} />
						)}
					</motion.div>
				))}
			</div>

			{/* 모바일: 세로 플로우 */}
			<VStack className="w-full items-center gap-0 px-[24px] md:hidden">
				{mobileNodes.map((node, index) => (
					<motion.div
						key={index}
						className="flex flex-col items-center"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: index * 0.25 }}
					>
						{index > 0 && (
							<motion.div
								className="py-[8px] text-ct-weak"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.3, delay: index * 0.25 }}
							>
								<Icon name="Down" width={20} height={20} />
							</motion.div>
						)}
						{node.type === "action" ? (
							<ActionNode name={node.name} description={node.description} gyulAmount={node.gyulAmount} />
						) : (
							<ControlNode name={node.name} description={node.description} />
						)}
					</motion.div>
				))}
			</VStack>

			{/* CTA 버튼 */}
			<Button
				variant="primary"
				size="lg"
				onClick={() => navigate({ to: "/workflow" })}
				whileHover={{ scale: 1.05 }}
				whileTap={{ scale: 0.95 }}
			>
				<HStack className="gap-[10px]">
					<Icon name="Action" width={24} height={24} />
					<span>{t("home.hero.cta")}</span>
				</HStack>
			</Button>
		</VStack>
	);
};
